import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import {
  FaFileAlt,
  FaTimes,
  FaUser,
  FaPhone,
  FaUserCheck,
  FaCalendarAlt,
  FaComments,
  FaComment,
  FaPaperPlane,
} from "react-icons/fa";
import config from "../../config";
import moment from "moment";

const TicketPopup = ({
  ticket,
  isOpen,
  onClose,
  currentUser,
  onTicketUpdate,
  showPopupMessage,
  socket,
}) => {
  const [tsoList, setTsoList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [comment, setComment] = useState("");
  const [selectedTso, setSelectedTso] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);

  useEffect(() => {
    if (isOpen && ticket) {
      fetchTsoList();
      fetchClientList();
      fetchComments();
    }

    return () => {
      setComments([]);
      setCommentsLoaded(false);
      setShowComments(false);
      setComment("");
      setSelectedTso("");
      setError(null);
    };
  }, [isOpen, ticket]);

  useEffect(() => {
    if (!socket || !ticket) return;

    const handleNewComment = (newComment) => {
      if (newComment.ticket_id === ticket.ticket_id) {
        setComments((prevComments) => [newComment, ...prevComments]);
      }
    };

    socket.on("ticket_comment", handleNewComment);

    return () => {
      socket.off("ticket_comment", handleNewComment);
    };
  }, [socket, ticket]);

  const fetchTsoList = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/tso-online/get-tso-and-tso-online-by-customer-id/${ticket.customer_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch TSO list");
      }

      const data = await response.json();
      setTsoList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching TSO list:", error);
      setError(error.message);
      setTsoList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientList = async () => {
    try {
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/tso-online/get-all-clients`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch client list");
      }

      const data = await response.json();
      setClientList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching client list:", error);
      setClientList([]);
    }
  };

  const fetchComments = async () => {
    if (!ticket?.ticket_id) return;

    try {
      setCommentsLoading(true);
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/tso-online/get-all-ticket-comments/${ticket.ticket_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch comments");
      }

      const data = await response.json();
      const commentsArray = Array.isArray(data) ? data : data?.response || [];
      setComments(commentsArray);
      setCommentsLoaded(true);
    } catch (error) {
      console.error("Error fetching comments:", error);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const getCommentAuthorName = (commenterId) => {
    if (!commenterId) return "Unknown";

    if (currentUser && commenterId === currentUser.id) {
      return currentUser.name || "You";
    }

    const tso = tsoList.find((t) => t.tso_id === commenterId);
    if (tso) return `${tso.tso_name} (TSO)`;

    const client = clientList.find((c) => c.id === commenterId);
    if (client) return `${client.customer_name} (Client)`;

    return "Unknown";
  };

  const handleAssignToMe = async () => {
    if (!currentUser?.id) {
      showPopupMessage("User information not loaded. Please refresh the page.");
      return;
    }

    try {
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/tso-online/assign-ticket-to-tso`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tso_id: currentUser.id,
            ticket_id: ticket.ticket_id,
            customer_id: ticket.customer_id,
            issue: ticket.issue,
            scheduled_date: ticket.ticket_created_date,
            ticket_type: ticket.ticket_type,
            ticket_priority: ticket.ticket_priority,
            product: ticket.product,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to assign ticket");
      }

      const statusResponse = await fetch(
        `${config.API_URL}/tso-online/change-ticket-status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ticket_id: ticket.ticket_id,
            ticket_status: "in_progress",
          }),
        }
      );

      if (!statusResponse.ok) {
        throw new Error("Failed to update ticket status");
      }

      showPopupMessage(
        "Ticket assigned to you and status updated to in progress!"
      );
      // Emit socket event to notify all clients about ticket assignment
      if (socket) {
        socket.emit("ticket_completed", {
          ticket_id: ticket.ticket_id,
          tso_id: currentUser.id,
          timestamp: new Date(),
        });
      }
      onTicketUpdate();
      onClose();
    } catch (error) {
      console.error("Error assigning ticket:", error);
      showPopupMessage(error.message);
    }
  };

  const handleTransfer = async () => {
    if (!selectedTso) {
      showPopupMessage("Please select a TSO to transfer the ticket");
      return;
    }

    try {
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/tso-online/assign-ticket-to-tso`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tso_id: selectedTso,
            ticket_id: ticket.ticket_id,
            customer_id: ticket.customer_id,
            issue: ticket.issue,
            scheduled_date: ticket.ticket_created_date,
            ticket_type: ticket.ticket_type,
            ticket_priority: ticket.ticket_priority,
            product: ticket.product,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to transfer ticket");
      }

      const statusResponse = await fetch(
        `${config.API_URL}/tso-online/change-ticket-status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ticket_id: ticket.ticket_id,
            ticket_status: "in_progress",
          }),
        }
      );

      if (!statusResponse.ok) {
        throw new Error("Failed to update ticket status");
      }

      showPopupMessage(
        "Ticket transferred successfully and status updated to in progress!"
      );
      // Emit socket event to notify all clients about ticket transfer
      if (socket) {
        socket.emit("ticket_completed", {
          ticket_id: ticket.ticket_id,
          tso_id: selectedTso,
          timestamp: new Date(),
        });
      }
      onTicketUpdate();
      onClose();
    } catch (error) {
      console.error("Error transferring ticket:", error);
      showPopupMessage(error.message);
    }
  };

  const handleSendComment = async () => {
    if (!comment.trim()) {
      showPopupMessage("Please enter a comment");
      return;
    }

    try {
      const token = Cookies.get("authToken");
      const response = await fetch(
        `${config.API_URL}/tso-online/add-ticket-comment`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ticket_id: ticket.ticket_id,
            comment: comment,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to add comment");
      }

      const result = await response.json();
      showPopupMessage("Comment added successfully!");
      setComment("");

      // Add the new comment to the list immediately
      if (result.comment) {
        setComments((prevComments) => [result.comment, ...prevComments]);
      } else {
        fetchComments();
      }

      onTicketUpdate();
    } catch (error) {
      console.error("Error adding comment:", error);
      showPopupMessage(error.message);
    }
  };

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString) return "Invalid Date";
    const date = moment(dateString);
    if (!date.isValid()) return "Invalid Date";

    if (showOnlyTime) {
      return date.format("hh:mm A");
    }
    return date.format("MM/DD/YYYY, hh:mm A");
  };

  const getPriorityBadge = (priority) => {
    if (!priority) return null;

    const priorityClasses = {
      high: "priority-high",
      medium: "priority-medium",
      normal: "priority-normal",
    };

    return (
      <span
        className={`priority-badge ${
          priorityClasses[priority.toLowerCase()] || ""
        }`}
      >
        {priority}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    if (!type) return null;

    return <span className="type-badge">{type}</span>;
  };

  const getProductBadge = (product) => {
    if (!product) return null;

    return <span className="product-badge">{product}</span>;
  };

  const toggleComments = () => {
    setShowComments(!showComments);
    if (!showComments && !commentsLoaded) {
      fetchComments();
    }
  };

  if (!isOpen || !ticket) return null;

  return (
    <div className="ticket-popup-overlay">
      <div className="ticket-popup-container">
        <div className="ticket-popup-header">
          <h2>
            <FaFileAlt className="ticket-info-icon" />
            View / Transfer / Comment to Ticket #{ticket.ticket_no}
            {ticket.ticket_type && getTypeBadge(ticket.ticket_type)}
            {ticket.ticket_priority && getPriorityBadge(ticket.ticket_priority)}
            {ticket.product && getProductBadge(ticket.product)}
          </h2>
          <button onClick={onClose} className="ticket-popup-close-btn">
            <FaTimes />
          </button>
        </div>

        <div className="ticket-popup-content">
          <div className="ticket-info-grid">
            <div className="ticket-info-item">
              <FaUser className="ticket-info-icon" />
              <div>
                <div className="ticket-info-label">Name</div>
                <div className="ticket-info-value">{ticket.customer_name}</div>
              </div>
            </div>
            <div className="ticket-info-item">
              <FaPhone className="ticket-info-icon" />
              <div>
                <div className="ticket-info-label">Phone</div>
                <div className="ticket-info-value">
                  {ticket.contact_person_phone_number || "-"}
                </div>
              </div>
            </div>
          </div>

          {ticket.filename && (
            <div className="ticket-info-item">
              <div className="ticket-info-label">Resources</div>
              <div className="ticket-resources-container">
                {ticket.filename.split(",").map((file, index) => (
                  <span key={index} className="ticket-resource-tag">
                    {file?.trim() || ""}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="ticket-report-card">
            <div className="ticket-report-label">
              Ticket Report by{" "}
              {ticket.created_by_user_type === "client"
                ? ticket.customer_name
                : ticket.created_by || "-"}
            </div>
            <div className="ticket-report-details">
              <h4 className="ticket-report-title">Details</h4>
              <p className="ticket-report-text">{ticket.issue}</p>
            </div>
          </div>

          <div className="ticket-info-grid">
            <div className="ticket-info-item">
              <FaUserCheck className="ticket-info-icon" />
              <div>
                <div className="ticket-info-label">Assigned to</div>
                <div className="ticket-info-value">
                  {ticket.assigned_tso_name || "Not assigned yet"}
                </div>
              </div>
            </div>
            <div className="ticket-info-item">
              <FaCalendarAlt className="ticket-info-icon" />
              <div>
                <div className="ticket-info-label">Date & Time</div>
                <div className="ticket-info-value">
                  {formatDateTime(ticket.ticket_created_date, false)}
                </div>
              </div>
            </div>
          </div>

          <div className="ticket-comments-toggle" onClick={toggleComments}>
            <FaComments className="ticket-info-icon" />
            <span>
              {showComments ? "Hide Comments" : "View Comments"} (
              {comments.length})
            </span>
          </div>

          {showComments && (
            <div className="ticket-comments-section">
              {commentsLoading ? (
                <div className="ticket-loading-state">Loading comments...</div>
              ) : comments.length > 0 ? (
                <div className="comments-list">
                  {comments.map((comment) => (
                    <div key={comment.comment_id} className="comment-item">
                      <p className="comment-text">
                        {comment.commnet || comment.comment}
                      </p>
                      <div className="comment-meta">
                        <span className="comment-author">
                          {comment.commented_by || "Unknown"}
                        </span>
                        <span className="comment-date">
                          {comment.commented_date
                            ? moment(comment.commented_date).format(
                                "MM/DD/YYYY, hh:mm A"
                              )
                            : "Invalid Date"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-comments">No comments yet</div>
              )}
            </div>
          )}

          <div className="ticket-comment-section">
            <div className="ticket-comment-header">
              <FaComment className="ticket-info-icon" />
              <div className="ticket-info-label">Add Comment</div>
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add your comment here..."
              className="ticket-comment-textarea"
              rows="4"
            />
          </div>

          <div className="ticket-transfer-section">
            <div className="ticket-info-label">Transfer to</div>
            {loading ? (
              <div className="ticket-loading-state">Loading TSO list...</div>
            ) : error ? (
              <div className="ticket-error-state">
                Error loading TSO list: {error}
              </div>
            ) : (
              <select
                value={selectedTso}
                onChange={(e) => setSelectedTso(e.target.value)}
                className="ticket-transfer-select"
                disabled={loading}
              >
                <option value="">Select TSO / Other online TSOs</option>
                {tsoList.map((tso) => (
                  <option key={tso.tso_id} value={tso.tso_id}>
                    {tso.tso_name} {tso.status ? `(${tso.status})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="ticket-popup-footer">
          <button
            onClick={handleAssignToMe}
            className="ticket-action-btn ticket-assign-btn"
          >
            <FaUserCheck />
            Assign to me
          </button>
          <button
            onClick={handleSendComment}
            className="ticket-action-btn ticket-comment-btn"
          >
            <FaComment />
            Send Comment
          </button>
          <button
            onClick={handleTransfer}
            className="ticket-action-btn ticket-transfer-btn"
            disabled={loading}
          >
            <FaPaperPlane />
            Transfer
          </button>
          <button
            onClick={onClose}
            className="ticket-action-btn ticket-close-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketPopup;
